/**
 * Mapa del recorrido.
 *
 * Se usa Leaflet con teselas raster en lugar de una libreria basada en WebGL:
 * funciona en mas dispositivos, incluidos moviles antiguos y navegadores con
 * aceleracion desactivada, y no depende de ninguna clave de API.
 *
 * Un mapa nunca es accesible por si mismo. Este componente se marca como
 * decorativo para lectores de pantalla y la informacion equivalente vive en la
 * lista de puntos que hay junto a el.
 */

import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { divIcon, latLngBounds } from 'leaflet';
import { useTranslation } from 'react-i18next';
import { formatearDistancia, type PuntoPublico } from '@recorregut/shared';
import { etiquetaFormato } from '@/i18n';
import type { PosicionActual } from '@/hooks/useGeolocalizacion.ts';

interface Props {
  puntos: PuntoPublico[];
  posicion: PosicionActual | null;
  visitados: Set<number>;
  respondidos: Set<number>;
  distancias: Map<number, number>;
  alAbrirPunto: (punto: PuntoPublico) => void;
  centrarEnPosicion: boolean;
}

/** Centro por defecto cuando aun no hay ni posicion ni puntos cargados. */
const CENTRO_POR_DEFECTO: [number, number] = [41.3874, 2.1686];

function iconoPunto(estado: 'pendiente' | 'visitado' | 'respondido', orden: number) {
  return divIcon({
    className: '',
    html: `<div class="marcador-punto marcador-${estado}" aria-hidden="true">${orden}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

const ICONO_POSICION = divIcon({
  className: '',
  html: '<div class="marcador-posicion" aria-hidden="true"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

/** Recentra el mapa cuando llega una posicion nueva, si se ha pedido. */
function Recentrado({ posicion, activo }: { posicion: PosicionActual | null; activo: boolean }) {
  const mapa = useMap();

  useEffect(() => {
    if (!activo || !posicion) return;
    mapa.setView([posicion.lat, posicion.lon], Math.max(mapa.getZoom(), 16), {
      // Sin animacion: mover el mapa solo, con animacion, marea a quien tiene
      // sensibilidad al movimiento.
      animate: false,
    });
  }, [mapa, posicion, activo]);

  return null;
}

/**
 * Encuadre inicial sobre todos los puntos del recorrido.
 *
 * Hace falta un componente porque `center` de MapContainer solo se aplica al
 * montarse: los puntos llegan despues, por consulta, y el mapa se quedaba en el
 * centro por defecto con los 27 puntos a dos kilometros, fuera de la vista.
 *
 * Se ejecuta una sola vez y solo mientras nadie ha activado la ubicacion: en
 * cuanto hay posicion manda `Recentrado`, que es lo que interesa caminando.
 */
function EncuadreInicial({ puntos, activo }: { puntos: PuntoPublico[]; activo: boolean }) {
  const mapa = useMap();
  const hecho = useRef(false);

  useEffect(() => {
    if (!activo || hecho.current || puntos.length === 0) return;
    hecho.current = true;

    const limites = latLngBounds(puntos.map((punto) => [punto.lat, punto.lon] as [number, number]));
    // Un solo punto no tiene area: `fitBounds` daria el zoom maximo y se veria
    // la acera. Con uno solo se centra y se deja un zoom de barrio.
    if (puntos.length === 1) {
      mapa.setView(limites.getCenter(), 16, { animate: false });
      return;
    }
    mapa.fitBounds(limites, { padding: [40, 40], animate: false });
  }, [mapa, puntos, activo]);

  return null;
}

/**
 * Saca del orden de tabulacion todo lo que Leaflet crea dentro del mapa.
 *
 * El mapa es decorativo y va marcado con `aria-hidden`, porque la version
 * navegable es la lista de puntos. Pero `aria-hidden` solo lo esconde del arbol
 * de accesibilidad: NO quita el foco de teclado. Medido el 08/09/2026 en un
 * navegador real: dentro de ese contenedor habia **32 elementos enfocables**
 * (el propio contenedor, que Leaflet hace enfocable para moverlo con flechas,
 * los 27 marcadores interactivos, los dos botones de zoom y el enlace de
 * atribucion). Con `aria-hidden` encima, quien navega con teclado y lector de
 * pantalla se encontraba 32 paradas mudas: el foco entra, no se anuncia nada, y
 * no hay forma de saber donde esta. La especificacion ARIA lo prohibe
 * expresamente y es un incumplimiento de WCAG 4.1.2.
 *
 * No sirve `inert`, que ademas bloquea el raton y dejaria el mapa muerto para
 * quien si lo usa. Lo que hace falta es exactamente `tabindex="-1"`: sigue
 * respondiendo al raton y al dedo, y desaparece del recorrido por tabulador.
 *
 * Va con un observador porque Leaflet crea y destruye nodos al vuelo: los
 * marcadores al cambiar de zoom y los globos al abrirlos y cerrarlos. Solo
 * observa `childList`, y lo que cambia son atributos, asi que no se realimenta.
 */
function SinFocoDeTeclado() {
  const mapa = useMap();

  useEffect(() => {
    const contenedor = mapa.getContainer();

    const quitarFoco = (): void => {
      contenedor.setAttribute('tabindex', '-1');
      contenedor
        .querySelectorAll<HTMLElement>('a[href], button, [tabindex]:not([tabindex="-1"])')
        .forEach((elemento) => {
          elemento.setAttribute('tabindex', '-1');
        });
    };

    quitarFoco();
    const observador = new MutationObserver(quitarFoco);
    observador.observe(contenedor, { childList: true, subtree: true });
    return () => {
      observador.disconnect();
    };
  }, [mapa]);

  return null;
}

export function MapaRecorrido({
  puntos,
  posicion,
  visitados,
  respondidos,
  distancias,
  alAbrirPunto,
  centrarEnPosicion,
}: Props) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  const centro = useMemo<[number, number]>(() => {
    if (posicion) return [posicion.lat, posicion.lon];
    const primero = puntos[0];
    return primero ? [primero.lat, primero.lon] : CENTRO_POR_DEFECTO;
  }, [posicion, puntos]);

  return (
    <>
      <div
      // `absolute inset-0` y no `h-full`: el contenedor de arriba marca el
      // hueco con `min-height` y un `height: 100%` contra eso da cero. Asi el
      // mapa llena el hueco real sea cual sea, y no depende de que el padre
      // declare una altura fija.
      className="absolute inset-0"
      // El mapa es una representacion alternativa de la lista de puntos, que si
      // es navegable. Anunciarlo entero seria ruido inutil.
      role="presentation"
      aria-hidden="true"
    >
      <MapContainer
        center={centro}
        zoom={15}
        scrollWheelZoom
        // La atribucion de Leaflet se apaga aqui y se pinta abajo, FUERA del
        // contenedor oculto: es una obligacion de la licencia de OpenStreetMap
        // y tiene que poder leerse y seguirse, no quedar dentro de un subarbol
        // marcado como decorativo.
        attributionControl={false}
        className="h-full w-full"
      >
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />

        <SinFocoDeTeclado />
        <EncuadreInicial puntos={puntos} activo={posicion === null} />
        <Recentrado posicion={posicion} activo={centrarEnPosicion} />

        {puntos.map((punto, indice) => {
          const estado = respondidos.has(punto.id)
            ? 'respondido'
            : visitados.has(punto.id)
              ? 'visitado'
              : 'pendiente';
          const distancia = distancias.get(punto.id);

          return (
            // Fragment y no div: dentro del mapa solo pueden vivir capas de
            // Leaflet, un elemento del DOM aqui rompe el arbol de capas.
            <Fragment key={punto.id}>
              <Circle
                center={[punto.lat, punto.lon]}
                radius={punto.radio_metros}
                pathOptions={{ color: 'var(--color-roig-600)', weight: 1, fillOpacity: 0.06 }}
              />
              <Marker
                position={[punto.lat, punto.lon]}
                icon={iconoPunto(estado, indice + 1)}
                eventHandlers={{
                  click: () => {
                    alAbrirPunto(punto);
                  },
                }}
              >
                <Popup>
                  <strong>{punto.nombre}</strong>
                  {distancia !== undefined ? (
                    <span className="mt-1 block text-sm">
                      {t('punt.distancia', { distancia: formatearDistancia(distancia, formato) })}
                    </span>
                  ) : null}
                </Popup>
              </Marker>
            </Fragment>
          );
        })}

        {posicion ? (
          <>
            <Marker position={[posicion.lat, posicion.lon]} icon={ICONO_POSICION} />
            {/* Circulo de precision: comunica de un vistazo si el GPS es fiable. */}
            <Circle
              center={[posicion.lat, posicion.lon]}
              radius={posicion.precision}
              pathOptions={{ color: 'var(--color-verd-600)', weight: 1, fillOpacity: 0.06 }}
            />
          </>
        ) : null}
      </MapContainer>
      </div>

      {/*
        Atribucion de OpenStreetMap, obligatoria por licencia. Va FUERA del
        contenedor decorativo, para que se pueda leer y seguir con teclado y con
        lector de pantalla: dentro habria quedado escondida por el `aria-hidden`
        del mapa. Se posiciona sobre la esquina, como la de Leaflet, pero es un
        elemento normal de la pagina.
      */}
      <p
        className="pointer-events-none absolute right-0 bottom-0 z-[400] bg-crema-50/90 px-1.5
                   py-0.5 text-[0.6875rem] text-tinta-700"
      >
        &copy;{' '}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
          className="pointer-events-auto text-roig-700 underline underline-offset-2"
        >
          OpenStreetMap
        </a>
      </p>
    </>
  );
}
